import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button as MuiButton,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  TextField,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationIcon,
  Visibility as ViewResultsIcon,
  Replay as ReplayIcon,
  Delete as DeleteIcon,
  Summarize as SummaryIcon,
} from '@mui/icons-material';
import { useApiClient } from '../../hooks/useApiClient';
import { useToast } from '../../context/ToastContext';
import ReadOnlyKeywordsPanel from '../../components/serp/ReadOnlyKeywordsPanel';
import BatchResultsDialog from '../../components/workflow-v2/BatchResultsDialog';
import CopyableText from '../../components/common/CopyableText';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const LOCATION_OPTIONS = [
  { code: 2840, name: 'United States' },
  { code: 2124, name: 'Canada' },
  { code: 2826, name: 'United Kingdom' },
  { code: 2036, name: 'Australia' },
  { code: 2276, name: 'Germany' },
  { code: 2250, name: 'France' },
  { code: 2356, name: 'India' },
  { code: 2392, name: 'Japan' },
  { code: 2076, name: 'Brazil' },
  { code: 2484, name: 'Mexico' },
];

const DataForSeoStep: React.FC = () => {
  const { callApi } = useApiClient();
  const { clientId, workflowId } = useParams<{ clientId: string; workflowId: string }>();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [keywords, setKeywords] = useState<string[]>([]);
  const [locationCode, setLocationCode] = useState(2840); // Default to US
  const [languageCode] = useState('en');
  
  // Initialize date range: from one year ago to today
  const getDefaultDateRange = () => {
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    
    return {
      from: oneYearAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDateRange();
  const [dateFrom, setDateFrom] = useState(defaultDates.from);
  const [dateTo, setDateTo] = useState(defaultDates.to);
  const [batchSize, setBatchSize] = useState(100); // Default to 100 keywords per batch
  const [isRunning, setIsRunning] = useState(false);
  const [isLoadingKeywords, setIsLoadingKeywords] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [showProgressPanel, setShowProgressPanel] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
  const [batchStatus, setBatchStatus] = useState<any>(null);
  const statusPollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [resultsDialogFilter, setResultsDialogFilter] = useState<'all' | 'completed' | 'failed'>('all');
  const [isResuming, setIsResuming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [reprocessPopoverAnchor, setReprocessPopoverAnchor] = useState<HTMLElement | null>(null);

  // Load keywords from SERP analysis and DataForSEO config on mount
  useEffect(() => {
    const loadConfigAndStatus = async () => {
      if (!clientId || !workflowId) {
        return;
      }

      setIsLoadingConfig(true);
      try {
        // First, get keywords from SERP analysis
        const serpResponse = await callApi<{
          has_config: boolean;
          config: any;
          module_id: number | null;
        }>(
          `/api/v1/workflow-v2/serp-analysis/config/${workflowId}`,
          {
            method: 'GET',
          }
        );

        if (serpResponse.has_config && serpResponse.config) {
          const loadedKeywords = serpResponse.config.keywords || [];
          
          if (loadedKeywords.length > 0) {
            setKeywords(loadedKeywords);
            console.log(`Loaded ${loadedKeywords.length} queries from SERP analysis`);
          }
        }

        // Then, get DataForSEO config and batch status
        const dataForSeoResponse = await callApi<{
          has_config: boolean;
          config: any;
          batch_job: any;
        }>(
          `/api/v1/workflow-v2/dataforseo-analysis/config/${workflowId}`,
          {
            method: 'GET',
          }
        );

        if (dataForSeoResponse.has_config && dataForSeoResponse.config) {
          const config = dataForSeoResponse.config;
          
          // Populate state with loaded config
          if (config.location_code) {
            setLocationCode(config.location_code);
          }
          
          if (config.date_from) {
            setDateFrom(config.date_from);
          }
          
          if (config.date_to) {
            setDateTo(config.date_to);
          }
          
          if (config.batch_size) {
            setBatchSize(config.batch_size);
          }
          
          // Load batch job status if available
          if (dataForSeoResponse.batch_job) {
            setCurrentBatchId(dataForSeoResponse.batch_job.batch_id);
            setBatchStatus(dataForSeoResponse.batch_job);
            setShowProgressPanel(true);
            
            // If the batch is still processing AND actively running, start polling
            if (dataForSeoResponse.batch_job.status === 'processing' && dataForSeoResponse.batch_job.is_active_running) {
              setIsRunning(true);
              
              statusPollingIntervalRef.current = setInterval(() => {
                pollBatchStatus(dataForSeoResponse.batch_job.batch_id);
              }, 2000); // Poll every 2 seconds
              
              // Initial poll
              pollBatchStatus(dataForSeoResponse.batch_job.batch_id);
            }
          }
        }
      } catch (error) {
        console.error('Error loading configuration:', error);
        // Don't show error toast - it's okay if there's no previous config
      } finally {
        setIsLoadingConfig(false);
      }
    };

    loadConfigAndStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, workflowId]);

  // Poll batch status
  const pollBatchStatus = async (batchId: string) => {
    try {
      const status = await callApi(`/api/v1/workflow-v2/dataforseo-analysis/status/${batchId}`, {
        method: 'GET'
      });
      
      setBatchStatus(status);
      
      // Stop polling if completed, failed, cancelled, or not actively running
      const shouldStopPolling = 
        status.status === 'completed' || 
        status.status === 'failed' ||
        status.status === 'cancelled' ||
        (status.status === 'processing' && !status.is_active_running);
      
      if (shouldStopPolling) {
        if (statusPollingIntervalRef.current) {
          clearInterval(statusPollingIntervalRef.current);
          statusPollingIntervalRef.current = null;
        }
        setIsRunning(false);
      }
    } catch (error: any) {
      console.error('Error polling batch status:', error);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (statusPollingIntervalRef.current) {
        clearInterval(statusPollingIntervalRef.current);
        statusPollingIntervalRef.current = null;
      }
    };
  }, []);

  const handleResumeBatch = async () => {
    if (!currentBatchId) return;
    
    setIsResuming(true);
    try {
      const response = await callApi(`/api/v1/workflow-v2/dataforseo-analysis/resume/${currentBatchId}`, {
        method: 'POST'
      });

      if (response) {
        showToast('DataForSEO Analysis resumed successfully', 'success');
        
        // Immediately fetch status to update UI
        await pollBatchStatus(currentBatchId);
        
        // Start polling for status updates
        statusPollingIntervalRef.current = setInterval(() => {
          pollBatchStatus(currentBatchId);
        }, 2000);
        
        setIsRunning(true);
        
        // Keep loading state true for a moment to ensure smooth transition
        setTimeout(() => {
          setIsResuming(false);
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error resuming DataForSEO analysis:', error);
      
      // Show appropriate message
      if (error.message?.includes('currently running') || error.message?.includes('actively processing')) {
        showToast('Batch is still running. Please wait a moment before retrying.', 'warning');
      } else {
        showToast(error.message || 'Failed to resume DataForSEO analysis', 'error');
      }
      
      setIsResuming(false);
    }
  };

  const handleCancelBatch = async () => {
    if (!currentBatchId) return;
    
    setShowCancelDialog(true);
  };

  const confirmCancelBatch = async () => {
    setShowCancelDialog(false);
    if (!currentBatchId) return;
    
    setIsCancelling(true);
    try {
      const response = await callApi(`/api/v1/workflow-v2/batch-job/cancel/${currentBatchId}`, {
        method: 'POST'
      });

      if (response) {
        showToast('DataForSEO Analysis cancelled successfully', 'info');
        
        // Stop polling immediately
        if (statusPollingIntervalRef.current) {
          clearInterval(statusPollingIntervalRef.current);
          statusPollingIntervalRef.current = null;
        }
        setIsRunning(false);
        
        // Fetch final status
        await pollBatchStatus(currentBatchId);
      }
    } catch (error: any) {
      console.error('Error cancelling DataForSEO analysis:', error);
      showToast(error.message || 'Failed to cancel DataForSEO analysis', 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleOpenReprocessPopover = (event: React.MouseEvent<HTMLElement>) => {
    const failedCount = batchStatus?.progress?.error_records || 0;
    if (failedCount === 0) {
      showToast('No failed queries to reprocess', 'info');
      return;
    }
    setReprocessPopoverAnchor(event.currentTarget);
  };

  const handleCloseReprocessPopover = () => {
    setReprocessPopoverAnchor(null);
  };

  const handleReprocessFailed = async (batchSize: number) => {
    if (!currentBatchId) return;
    
    // Close popover
    handleCloseReprocessPopover();
    setIsReprocessing(true);
    
    try {
      const response = await callApi<{
        success: boolean;
        message: string;
        failed_keyword_count: number;
      }>(`/api/v1/workflow-v2/dataforseo-analysis/reprocess-failed/${currentBatchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          batch_size: batchSize
        })
      });

      if (response) {
        showToast(
          `Reprocessing ${response.failed_keyword_count} failed queries with batch size ${batchSize}...`, 
          'success'
        );
        
        // Immediately fetch status to update UI
        await pollBatchStatus(currentBatchId);
        
        // Start polling for status updates
        statusPollingIntervalRef.current = setInterval(() => {
          pollBatchStatus(currentBatchId);
        }, 2000);
        
        setIsRunning(true);
        
        // Keep loading state true for a moment to ensure smooth transition
        setTimeout(() => {
          setIsReprocessing(false);
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error reprocessing failed queries:', error);
      
      // Show appropriate message
      if (error.message?.includes('currently running') || error.message?.includes('actively processing')) {
        showToast('Batch is still running. Please wait a moment before retrying.', 'warning');
      } else if (error.message?.includes('No failed queries')) {
        showToast('No failed queries found to reprocess', 'info');
      } else {
        showToast(error.message || 'Failed to reprocess queries', 'error');
      }
      
      setIsReprocessing(false);
    }
  };

  const handleDeleteKeyword = (keywordToDelete: string) => {
    setKeywords(prevKeywords => prevKeywords.filter(keyword => keyword !== keywordToDelete));
    showToast(`Removed query: "${keywordToDelete}"`, 'success');
  };

  const handleGoToSummary = () => {
    const summaryPath = `/workflow2/${clientId}/${workflowId}/summary`;
    navigate(summaryPath);
  };

  const handleRunAnalysis = async () => {
    if (!clientId || !workflowId) {
      showToast('Client ID and Workflow ID are required', 'error');
      return;
    }

    if (keywords.length === 0) {
      showToast('No queries available. Please complete previous steps.', 'warning');
      return;
    }

    // Hide previous result panel immediately
    setShowProgressPanel(false);
    setBatchStatus(null);
    setIsRunning(true);

    try {
      // Start the analysis
      const response = await callApi<{
        batch_id: string;
        message: string;
        total_keywords: number;
      }>(
        `/api/v1/workflow-v2/${workflowId}/dataforseo`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: clientId,
            workflow_id: workflowId,
            keywords: keywords,
            location_code: locationCode,
            language_code: languageCode,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            batch_size: batchSize,
          }),
        }
      );

      setCurrentBatchId(response.batch_id);
      setShowProgressPanel(true);
      setBatchStatus({
        batch_id: response.batch_id,
        status: 'processing',
        progress: {
          total_records: response.total_keywords,
          processed_records: 0,
          error_records: 0,
          percentage: 0
        }
      });
      
      showToast(
        `DataForSEO analysis started! Processing ${response.total_keywords} queries.`,
        'success'
      );
      
      // Start polling for status updates
      statusPollingIntervalRef.current = setInterval(() => {
        pollBatchStatus(response.batch_id);
      }, 2000); // Poll every 2 seconds
      
      // Initial poll
      pollBatchStatus(response.batch_id);

    } catch (error: any) {
      console.error('Error running DataForSEO analysis:', error);
      showToast(
        error?.message || 'Failed to run DataForSEO analysis',
        'error'
      );
      setIsRunning(false);
    }
  };

  // Show loading spinner during config loading
  if (isLoadingConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading DataForSEO configuration...
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body1" color="text.secondary" paragraph>
      Get monthly search volume from DataforSEO for your sampled queries
      </Typography>

      {/* Top Section - Keywords Panel */}
      <Box>
        <ReadOnlyKeywordsPanel 
          keywords={keywords}
          loading={isLoadingConfig || isLoadingKeywords}
          onDeleteKeyword={handleDeleteKeyword}
        />
      </Box>

      {/* Bottom Section - Analysis Configuration and Run Button */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <TrendingUpIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Analysis Configuration
          </Typography>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          Configure the location, batch size, and date range for search volume analysis. 
          Batch size determines how many queries are sent per API request. Smaller batches are more reliable but slower.
          Queries are automatically loaded from your workflow.
        </Alert>

        {/* Configuration Form */}
        <Grid container spacing={3}>
          {/* Location Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={locationCode}
                onChange={(e) => setLocationCode(e.target.value as number)}
                label="Location"
                disabled={isRunning}
                startAdornment={
                  <LocationIcon sx={{ mr: 1, color: 'action.active' }} />
                }
              >
                {LOCATION_OPTIONS.map((loc) => (
                  <MenuItem key={loc.code} value={loc.code}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Batch Size Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Batch Size</InputLabel>
              <Select
                value={batchSize}
                onChange={(e) => setBatchSize(e.target.value as number)}
                label="Batch Size"
                disabled={isRunning}
              >
                <MenuItem value={10}>10 queries (Safest)</MenuItem>
                <MenuItem value={25}>25 queries (Conservative)</MenuItem>
                <MenuItem value={50}>50 queries (Balanced)</MenuItem>
                <MenuItem value={100}>100 queries (Recommended)</MenuItem>
                <MenuItem value={200}>200 queries (Aggressive)</MenuItem>
                <MenuItem value={500}>500 queries (Maximum)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Range - From */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="From Date (Optional)"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              disabled={isRunning}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Date Range - To */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="To Date (Optional)"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              disabled={isRunning}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>

        {/* Run Button Section */}
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <MuiButton
            variant="contained"
            size="large"
            onClick={handleRunAnalysis}
            disabled={
              keywords.length === 0 || 
              isRunning ||
              (batchStatus && batchStatus.status === 'processing' && batchStatus.is_active_running)
            }
            startIcon={
              isRunning ? <CircularProgress size={20} /> : <TrendingUpIcon />
            }
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              background: 'linear-gradient(45deg, #332E5C, #00CED1)',
              '&:hover': {
                background: 'linear-gradient(45deg, #2a2650, #00b8c4)',
              },
              '&:disabled': {
                background: alpha(theme.palette.action.disabled, 0.12),
              },
            }}
          >
            {(isRunning || (batchStatus && batchStatus.status === 'processing' && batchStatus.is_active_running))
              ? 'Analysis Running...' 
              : `Run DataForSEO Search Volume Tool${keywords.length > 0 ? ` - ${keywords.length} queries` : ''}`}
          </MuiButton>
        </Box>
      </Paper>

      {/* Progress Status Panel */}
      {showProgressPanel && batchStatus && (
        <Card 
          sx={{ 
            borderLeft: 4, 
            borderColor: 
              batchStatus.status === 'completed' ? 'success.main' : 
              batchStatus.status === 'failed' ? 'error.main' :
              batchStatus.status === 'cancelled' ? 'grey.500' :
              'primary.main'
          }}
        >
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">
                  DataForSEO Analysis Progress
                </Typography>
                <Chip 
                  label={batchStatus.status.toUpperCase()} 
                  color={
                    batchStatus.status === 'completed' ? 'success' : 
                    batchStatus.status === 'failed' ? 'error' :
                    batchStatus.status === 'cancelled' ? 'default' :
                    'primary'
                  }
                  size="small"
                />
                {batchStatus.batch_id && (
                  <Tooltip title="Review batch results" arrow>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setResultsDialogFilter('all');
                        setResultsDialogOpen(true);
                      }}
                      sx={{ 
                        padding: 0.5,
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                      }}
                    >
                      <ViewResultsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <CopyableText text={batchStatus.batch_id} label="Batch ID" />
            </Box>

            {/* Progress Bar */}
            <Box sx={{ mb: 2 }}>
              {(() => {
                const progressPct = Math.min(100, batchStatus.progress.percentage);
                const processed = Math.min(batchStatus.progress.processed_records, batchStatus.progress.total_records);
                return (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        {processed} / {batchStatus.progress.total_records} queries processed
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {progressPct}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progressPct}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </>
                );
              })()}
            </Box>

            {/* Statistics Grid */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    {batchStatus.progress.total_records}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total queries
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main">
                    {batchStatus.progress.processed_records - batchStatus.progress.error_records}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Successful
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="h6" color="error.main">
                      {batchStatus.progress.error_records}
                    </Typography>
                    {batchStatus.progress.error_records > 0 && (
                      <Tooltip title="View failed queries" arrow>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setResultsDialogFilter('failed');
                            setResultsDialogOpen(true);
                          }}
                          sx={{ 
                            padding: 0.25,
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                          }}
                        >
                          <ViewResultsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Error Message - Parse task-level errors if JSON */}
            {batchStatus.error_message && (() => {
              try {
                const taskErrors = JSON.parse(batchStatus.error_message);
                if (Array.isArray(taskErrors) && taskErrors.length > 0) {
                  return (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Task-Level Errors ({taskErrors.length})
                      </Typography>
                      {taskErrors.slice(0, 3).map((error: any, index: number) => (
                        <Box key={index} sx={{ mt: 1 }}>
                          <Typography variant="caption" component="div">
                            <strong>Batch {error.batch_num}:</strong> {error.status_message}
                          </Typography>
                          {error.keyword_count && (
                            <Typography variant="caption" color="text.secondary">
                              Affected queries: {error.keyword_count}
                            </Typography>
                          )}
                        </Box>
                      ))}
                      {taskErrors.length > 3 && (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                          ... and {taskErrors.length - 3} more errors
                        </Typography>
                      )}
                    </Alert>
                  );
                }
              } catch (e) {
                // Not JSON, display as plain text
              }
              return (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {batchStatus.error_message}
                </Alert>
              );
            })()}

            {/* Show info if batch is actively running */}
            {batchStatus.status === 'processing' && batchStatus.is_active_running && (
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Batch is actively processing
                </Typography>
                <Typography variant="body2">
                  The batch is currently running in the background. Progress updates will appear automatically.
                </Typography>
              </Alert>
            )}

            {/* Show warning if status is processing but not actively running */}
            {batchStatus.status === 'processing' && !batchStatus.is_active_running && (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Batch appears to be stuck
                </Typography>
                <Typography variant="body2">
                  The batch is marked as processing but is not actively running. 
                  It may have been interrupted by a server restart or crash.
                  You can resume to restart processing from where it stopped.
                </Typography>
              </Alert>
            )}

            {/* Action Buttons - Resume and Cancel */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              {/* Resume Button - Show when not actively running and not completed */}
              {!batchStatus.is_active_running && 
               batchStatus.status !== 'completed' && (
                  <MuiButton
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={
                      isResuming 
                        ? <CircularProgress size={20} color="inherit" /> 
                        : <ReplayIcon />
                    }
                    onClick={handleResumeBatch}
                    disabled={isResuming || isCancelling}
                    sx={{ minWidth: 200 }}
                  >
                    {isResuming ? 'Resuming...' : 'Resume Batch Process'}
                  </MuiButton>
                )}
                
                {/* Cancel Button - Show when actively running */}
                {batchStatus.status === 'processing' && batchStatus.is_active_running && (
                  <MuiButton
                    variant="outlined"
                    color="error"
                    size="large"
                    startIcon={
                      isCancelling 
                        ? <CircularProgress size={20} color="inherit" /> 
                        : <DeleteIcon />
                    }
                    onClick={handleCancelBatch}
                    disabled={isCancelling || isResuming}
                    sx={{ minWidth: 200 }}
                  >
                  {isCancelling ? 'Cancelling...' : 'Cancel Batch Process'}
                </MuiButton>
              )}
            </Box>

            {/* Completion Message */}
            {batchStatus.status === 'completed' && (
              <>
                {/* Only show success message if there's at least 1 successful keyword */}
                {(batchStatus.progress.processed_records - batchStatus.progress.error_records) > 0 && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      DataForSEO Analysis completed successfully! {batchStatus.progress.processed_records - batchStatus.progress.error_records} out of {batchStatus.progress.total_records} queries were successful.
                    </Typography>
                  </Alert>
                )}
                
                {/* Action Buttons - Reprocess and Go to Summary */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                  {/* Reprocess Failed Keywords Button - Show when completed with failures */}
                  {batchStatus.progress.error_records > 0 && !batchStatus.is_active_running && (
                    <MuiButton
                      variant="outlined"
                      color="warning"
                      size="large"
                      startIcon={
                        isReprocessing 
                          ? <CircularProgress size={20} color="inherit" /> 
                          : <ReplayIcon />
                      }
                      onClick={handleOpenReprocessPopover}
                      disabled={isReprocessing || isRunning}
                      sx={{ minWidth: 200 }}
                    >
                      {isReprocessing 
                        ? 'Reprocessing...' 
                        : `Reprocess ${batchStatus.progress.error_records} Failed Queries`}
                    </MuiButton>
                  )}
                  
                  {/* Go to Workflow Summary Button */}
                  <MuiButton
                    variant="contained"
                    size="large"
                    startIcon={<SummaryIcon />}
                    onClick={handleGoToSummary}
                    sx={{
                      minWidth: 200,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                      boxShadow: `0 3px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                      },
                    }}
                  >
                    Go to Workflow Summary
                  </MuiButton>
                </Box>
              </>
            )}

            {/* Cancellation Message */}
            {batchStatus.status === 'cancelled' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  DataForSEO Analysis was cancelled. {batchStatus.progress.processed_records - batchStatus.progress.error_records} out of {batchStatus.progress.total_records} queries were completed before cancellation.
                </Typography>
              </Alert>
            )}

            {/* Info - Only show when not completed and not cancelled */}
            {batchStatus.status !== 'completed' && batchStatus.status !== 'cancelled' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This process may take several minutes depending on the number of queries.
                  You can safely close this page - the processing will continue in the background.
                  {(batchStatus.status === 'failed' || (batchStatus.status === 'processing' && !batchStatus.is_active_running)) && 
                    ' If processing is interrupted, you can resume from where it stopped using the Resume button above.'}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reprocess Batch Size Selection Popover */}
      <Popover
        open={Boolean(reprocessPopoverAnchor)}
        anchorEl={reprocessPopoverAnchor}
        onClose={handleCloseReprocessPopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              maxWidth: 400,
              mt: -1
            }
          }
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Select Batch Size
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {batchStatus?.progress?.error_records || 0} failed queries to reprocess
          </Typography>
        </Box>
        <Divider />
        <List sx={{ py: 0 }}>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleReprocessFailed(1)}
              sx={{ py: 1.5 }}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    1 query per batch
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    Safest - Slowest, but most reliable
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleReprocessFailed(3)}
              sx={{ py: 1.5 }}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    3 queries per batch
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    Balanced - Good speed and reliability
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
          <Divider />
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleReprocessFailed(5)}
              sx={{ py: 1.5 }}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    5 queries per batch
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    Faster - May encounter issues again
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>

      {/* Batch Results Dialog */}
      {batchStatus?.batch_id && (
        <BatchResultsDialog
          open={resultsDialogOpen}
          onClose={() => setResultsDialogOpen(false)}
          batchId={batchStatus.batch_id}
          initialFilter={resultsDialogFilter}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        title="Cancel DataForSEO Analysis"
        message="Are you sure you want to cancel this DataForSEO analysis? Currently processing queries will complete, but no new queries will be started. This action cannot be undone."
        confirmText="Yes, Cancel Analysis"
        cancelText="No, Keep Running"
        confirmColor="error"
        onConfirm={confirmCancelBatch}
        onCancel={() => setShowCancelDialog(false)}
      />
    </Box>
  );
};

export default DataForSeoStep;

